'use client';

import Image from 'next/image';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { buildPublicUrl } from '@/lib/r2-urls';
import {
    buildResponsiveSrcSet,
    getExtension,
    getSizesForLayout,
    isCdnUrl,
    stripExtension,
} from '@/lib/responsive-image';

const DEFAULT_PRELOAD_MARGIN = '650px 0px 650px 0px';

const preloadedSources = new Set();
const preloadingImages = new Map();

function markSourceLoaded(src) {
    if (!src) return;
    preloadingImages.delete(src);
    preloadedSources.add(src);
}

function hasSourceLoaded(src) {
    if (!src) return false;
    return preloadedSources.has(src);
}

function resolveRootMargin(value) {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return `${value}px 0px ${value}px 0px`;
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return DEFAULT_PRELOAD_MARGIN;
        const parts = trimmed.split(/\s+/);
        if (parts.length === 1) {
            return `${trimmed} 0px ${trimmed} 0px`;
        }
        if (parts.length === 2 || parts.length === 4) {
            return trimmed;
        }
        if (parts.length === 3) {
            return `${parts[0]} ${parts[1]} ${parts[2]} ${parts[1]}`;
        }
        return trimmed;
    }

    return DEFAULT_PRELOAD_MARGIN;
}

function preloadSource(src, crossOrigin) {
    if (!src || typeof window === 'undefined') return;
    if (preloadingImages.has(src) || hasSourceLoaded(src)) return;

    try {
        const img = new window.Image();
        if (crossOrigin) {
            try {
                img.crossOrigin = crossOrigin;
            } catch (_e) {
                // ignore assignment issues (some browsers restrict it)
            }
        }
        try {
            img.decoding = 'async';
        } catch (_e) {
            // decoding hint unsupported – ignore
        }
        preloadingImages.set(src, img);

        let timedOut = false;
        const TIMEOUT_MS = 8000;
        const timeoutId =
            typeof window !== 'undefined'
                ? window.setTimeout(() => {
                      timedOut = true;
                      try {
                          img.src = ''; // abort
                      } catch (_e) {}
                      markSourceLoaded(src);
                      if (
                          process.env &&
                          process.env.NODE_ENV !== 'production'
                      ) {
                          // eslint-disable-next-line no-console
                          console.warn(
                              '[AppImage] preloadSource timed out',
                              src,
                          );
                      }
                  }, TIMEOUT_MS)
                : null;

        const finalize = () => {
            if (timeoutId != null && typeof window !== 'undefined')
                try {
                    window.clearTimeout(timeoutId);
                } catch (_e) {}
            if (!timedOut) {
                markSourceLoaded(src);
            }
        };

        img.onload = finalize;
        img.onerror = finalize;
        try {
            img.src = src;
        } catch (_e) {
            // If setting src throws, mark loaded so we don't spin
            finalize();
        }
    } catch (_e) {
        // If constructing Image fails, fall back to marking as loaded so we don't spin.
        markSourceLoaded(src);
    }
}

function ensureAltText(alt, fallback = 'Image') {
    if (typeof alt === 'string' && alt.trim().length > 0) return alt;
    return fallback;
}

function mergeClassNames(base, incoming) {
    if (!incoming) return base;
    if (!base) return incoming;
    return `${base} ${incoming}`;
}

const AppImage = forwardRef(function AppImage(
    {
        src,
        alt,
        width,
        height,
        fill,
        sizes,
        priority = false,
        priorityForLCP = false,
        placeholder = 'empty',
        blurDataURL,
        className,
        loading,
        fetchPriority,
        unoptimized,
        resolvePublicUrl = true,
        // Default quality of 70 (instead of Next.js default of 75) provides
        // better compression while maintaining acceptable visual quality.
        // Can be overridden per-image when higher quality is needed.
        quality = 70,
        onLoad,
        onError,
        crossOrigin,
        enablePreload = true,
        preloadRootMargin = DEFAULT_PRELOAD_MARGIN,
        // Skip the opacity:0 → opacity:1 fade transition for critical above-the-fold
        // images. When true (or when priority is set), the image is visible immediately
        // to prevent the "janky" staggered load effect on first page visit.
        skipFadeIn,
        // Layout preset for auto-deriving the `sizes` attribute.
        // One of: 'hero', 'card', 'gallery', 'cover', 'full-width'
        // Only used when `sizes` prop is not explicitly provided.
        layout,
        // Custom responsive widths for generated srcSet (default: RESPONSIVE_WIDTHS).
        // Only used when the image is CDN-hosted with an extension and no width-variant
        // pattern is already present in the URL.
        widths,
        // Set to false to disable automatic srcSet generation for CDN images.
        // Use for images that don't have pre-generated responsive variants on the CDN
        // (e.g. user-uploaded post cover images) to avoid 404s for missing variants.
        responsive = true,
        // Override the file extension used in generated srcSet URLs.
        // When the original file format differs from the served variant format
        // (e.g. original is .jpg but variants are .webp), set this to the variant
        // extension so srcSet entries point to the correct files.
        srcSetExt,
        ...rest
    },
    ref,
) {
    const inputSrc = typeof src === 'string' ? src : src?.src || '';
    const hasInputSrc = Boolean(inputSrc);
    const resolvedSrc = hasInputSrc
        ? resolvePublicUrl
            ? buildPublicUrl(inputSrc)
            : inputSrc
        : '';

    const resolvedAlt = ensureAltText(alt);
    const isPriority = priorityForLCP || priority;
    const resolvedFetchPriority =
        fetchPriority || (isPriority ? 'high' : undefined);
    const resolvedLoading = isPriority ? undefined : loading || 'lazy';
    const hasBlur = placeholder === 'blur' && !!blurDataURL;
    // Avoid using blur placeholder for LCP / priority images to prevent a flash
    // during hydration when server and client handoff the same image.
    const shouldSuppressBlurForPriority = Boolean(priorityForLCP || priority);
    const effectiveHasBlur = shouldSuppressBlurForPriority ? false : hasBlur;
    const resolvedPlaceholder = effectiveHasBlur ? 'blur' : 'empty';
    const resolvedBlurData = effectiveHasBlur ? blurDataURL : undefined;

    // Auto-derive sizes from layout preset if not explicitly provided.
    const resolvedSizes =
        sizes || (layout ? getSizesForLayout(layout) : undefined);

    // Bypass Next.js Image Optimization for CDN images. These images are
    // served from the CDN which handles resizing, so routing through
    // /_next/image adds unnecessary server memory usage (Sharp processing)
    // and latency. Pass unoptimized={true} explicitly for SVGs or other
    // special cases.
    const resolvedUnoptimized =
        typeof unoptimized === 'boolean' ? unoptimized : isCdnUrl(resolvedSrc);

    // Check if this is a CDN URL with an extension (not extensionless) and
    // does NOT already include a width variant pattern (e.g. -800w.webp).
    // In that case we generate a responsive srcSet for the single format.
    const { srcSet: explicitSrcSet, ...forwardableRest } = rest;
    let resolvedSrcSet = explicitSrcSet;
    if (!resolvedSrcSet && responsive && isCdnUrl(resolvedSrc)) {
        const ext =
            srcSetExt && typeof srcSetExt === 'string'
                ? srcSetExt.replace(/^\./, '')
                : getExtension(resolvedSrc);
        if (ext && ext !== 'svg' && !/-\d+w\.\w+$/.test(resolvedSrc)) {
            const cleanWidths =
                Array.isArray(widths) && widths.length > 0 ? widths : undefined;
            const basePath = stripExtension(resolvedSrc);
            resolvedSrcSet = buildResponsiveSrcSet(basePath, ext, cleanWidths);
        }
    }

    // For priority images or when skipFadeIn is explicitly true, skip the fade-in
    // animation by not applying the base 'app-image' class (which starts at opacity:0).
    // This ensures critical above-the-fold images are visible immediately.
    const shouldSkipFade =
        skipFadeIn === true || (skipFadeIn !== false && isPriority);
    const finalClassName = shouldSkipFade
        ? className
        : mergeClassNames('app-image', className);

    const internalRef = useRef(null);
    const hasPrefetchedRef = useRef(false);
    const [loadState, setLoadState] = useState({ loaded: false, error: false });
    const normalizedRootMargin = resolveRootMargin(preloadRootMargin);

    const boostImagePriority = useCallback(() => {
        const node = internalRef.current;
        if (!node) return;
        try {
            if (typeof node.setAttribute === 'function') {
                node.setAttribute('loading', 'eager');
                node.setAttribute('fetchpriority', 'high');
            }
            if ('loading' in node) {
                node.loading = 'eager';
            }
            if ('fetchPriority' in node) {
                node.fetchPriority = 'high';
            }
        } catch (_e) {
            // ignore upgrade issues
        }
    }, []);

    const handleLoad = useCallback(
        (event) => {
            const target =
                event?.currentTarget || event?.target || internalRef.current;
            if (target?.dataset?.appImageHasLoaded === 'true') {
                if (!loadState.loaded) {
                    setLoadState((prev) =>
                        prev.loaded ? prev : { ...prev, loaded: true },
                    );
                }
                if (typeof onLoad === 'function') {
                    onLoad(event);
                }
                return;
            }
            if (target) {
                try {
                    target.dataset.appImageHasLoaded = 'true';
                    target.dataset.appImageLastSrc = resolvedSrc;
                } catch (_e) {
                    // ignore data-set failures
                }
            }
            hasPrefetchedRef.current = true;
            markSourceLoaded(resolvedSrc);
            setLoadState((prev) =>
                prev.loaded ? prev : { ...prev, loaded: true, error: false },
            );
            if (typeof onLoad === 'function') {
                onLoad(event);
            }
        },
        [loadState.loaded, onLoad, resolvedSrc],
    );

    const handleError = useCallback(
        (event) => {
            const target =
                event?.currentTarget || event?.target || internalRef.current;
            if (target?.dataset?.appImageHasLoaded === 'true') {
                if (typeof onError === 'function') {
                    onError(event);
                }
                return;
            }
            hasPrefetchedRef.current = true;
            markSourceLoaded(resolvedSrc);
            setLoadState((prev) => ({ ...prev, error: true }));
            if (process.env && process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.warn(
                    '[AppImage] handleError',
                    resolvedSrc,
                    event?.nativeEvent || event,
                );
            }
            if (
                typeof process !== 'undefined' &&
                process.env &&
                process.env.NODE_ENV !== 'production'
            ) {
                try {
                    // Use the project's structured JSON logging format for development warnings
                    // Keep messages short and non-sensitive.
                    // eslint-disable-next-line no-console
                    console.warn(
                        JSON.stringify({
                            ts: new Date().toISOString(),
                            level: 'warn',
                            service: 'app_image',
                            event: 'load_error',
                            src: resolvedSrc || null,
                            message: String(
                                event?.nativeEvent || event || 'failed to load',
                            ).slice(0, 240),
                        }),
                    );
                } catch (_e) {
                    // ignore logging issues
                }
            }
            if (typeof onError === 'function') {
                onError(event);
            }
        },
        [onError, resolvedSrc],
    );

    const setRefs = useCallback(
        (node) => {
            internalRef.current = node;
            if (typeof ref === 'function') {
                ref(node);
            } else if (ref && typeof ref === 'object') {
                ref.current = node;
            }
        },
        [ref],
    );

    useEffect(() => {
        const node = internalRef.current;
        if (!node) return;

        const nativeLoad = (nativeEvent) => {
            handleLoad(nativeEvent);
        };

        const nativeError = (nativeEvent) => {
            handleError(nativeEvent);
        };

        node.addEventListener('load', nativeLoad);
        node.addEventListener('error', nativeError);

        try {
            if (node.dataset.appImageLastSrc !== resolvedSrc) {
                node.dataset.appImageHasLoaded = '';
                node.dataset.appImageLastSrc = resolvedSrc;
            }
        } catch (_e) {
            // ignore dataset errors
        }

        if (node.complete && node.naturalWidth > 0) {
            if (node.dataset.appImageHasLoaded === 'true') {
                if (!loadState.loaded) {
                    setLoadState((prev) =>
                        prev.loaded
                            ? prev
                            : { ...prev, loaded: true, error: false },
                    );
                }
                return () => {
                    node.removeEventListener('load', nativeLoad);
                    node.removeEventListener('error', nativeError);
                };
            }
            handleLoad({ currentTarget: node });
        }

        return () => {
            node.removeEventListener('load', nativeLoad);
            node.removeEventListener('error', nativeError);
        };
    }, [resolvedSrc, handleLoad, handleError, loadState.loaded]);

    useEffect(() => {
        hasPrefetchedRef.current = isPriority || hasSourceLoaded(resolvedSrc);
        const node = internalRef.current;
        const alreadyComplete = node?.complete && node.naturalWidth > 0;
        if (!alreadyComplete) {
            setLoadState({ loaded: false, error: false });
        }
    }, [resolvedSrc, isPriority]);

    // Some browsers (notably Chrome) can behave inconsistently with load events
    // for SVG assets when using the Next/Image wrapper. If an SVG is used we
    // proactively mark it as loaded so CSS that depends on the
    // `.app-image--loaded` class (opacity: 1) doesn't leave the image
    // invisible. This is a safe fallback because SVGs are typically
    // instantaneous to render and don't require the same decoding flow as
    // raster images.
    useEffect(() => {
        if (!resolvedSrc) return;
        try {
            const isSvg = /\.(svg|svgz)(\?.*)?$/i.test(resolvedSrc);
            if (isSvg) {
                // mark loaded if not already
                setLoadState((prev) =>
                    prev.loaded
                        ? prev
                        : { ...prev, loaded: true, error: false },
                );
                markSourceLoaded(resolvedSrc);
            }
        } catch (_e) {
            // ignore regex or state errors
        }
    }, [resolvedSrc]);

    useEffect(() => {
        if (!resolvedSrc || !isPriority) return;
        boostImagePriority();
        preloadSource(resolvedSrc, crossOrigin);
        hasPrefetchedRef.current = true;
    }, [resolvedSrc, isPriority, boostImagePriority, crossOrigin]);

    useEffect(() => {
        if (!resolvedSrc) return;
        if (!enablePreload || isPriority) return;

        if (hasSourceLoaded(resolvedSrc)) {
            hasPrefetchedRef.current = true;
            return;
        }

        const triggerPreload = () => {
            if (hasPrefetchedRef.current) return;
            boostImagePriority();
            preloadSource(resolvedSrc, crossOrigin);
            hasPrefetchedRef.current = true;
        };

        const node = internalRef.current;

        if (typeof window === 'undefined') {
            return;
        }

        if (!node || typeof IntersectionObserver === 'undefined') {
            triggerPreload();
            return;
        }

        const observer = new IntersectionObserver(
            (entries, obs) => {
                for (const entry of entries) {
                    if (entry.isIntersecting || entry.intersectionRatio > 0) {
                        triggerPreload();
                        break;
                    }
                }
                if (hasPrefetchedRef.current) {
                    obs.disconnect();
                }
            },
            { rootMargin: normalizedRootMargin },
        );

        observer.observe(node);

        return () => observer.disconnect();
    }, [
        resolvedSrc,
        enablePreload,
        isPriority,
        crossOrigin,
        boostImagePriority,
        normalizedRootMargin,
    ]);

    const computedClassName = mergeClassNames(
        finalClassName,
        // When skipping fade, always mark as loaded for consistent styling
        shouldSkipFade || loadState.loaded
            ? 'app-image--loaded'
            : loadState.error
              ? 'app-image--error'
              : '',
    );

    if (!hasInputSrc) {
        return null;
    }

    const imageProps = {
        ref: setRefs,
        src: resolvedSrc,
        className: computedClassName,
        priority: isPriority,
        quality,
        ...(resolvedFetchPriority
            ? { fetchPriority: resolvedFetchPriority }
            : {}),
        ...(resolvedLoading ? { loading: resolvedLoading } : {}),
        placeholder: resolvedPlaceholder,
        ...(resolvedBlurData ? { blurDataURL: resolvedBlurData } : {}),
        unoptimized: resolvedUnoptimized,
        sizes: resolvedSizes,
        ...(resolvedSrcSet ? { srcSet: resolvedSrcSet } : {}),
        ...(crossOrigin ? { crossOrigin } : {}),
        ...forwardableRest,
    };

    if (fill) {
        imageProps.fill = true;
    } else {
        imageProps.width = width;
        imageProps.height = height;
    }

    // When we have a custom srcSet for CDN images, use a plain <img> tag
    // instead of next/image. This is necessary because next/image strips
    // custom srcSet and sizes when unoptimized={true}.
    if (resolvedSrcSet) {
        return (
            <img
                ref={setRefs}
                alt={resolvedAlt}
                src={resolvedSrc}
                srcSet={resolvedSrcSet}
                sizes={resolvedSizes || '100vw'}
                className={computedClassName}
                loading={resolvedLoading || undefined}
                fetchPriority={resolvedFetchPriority || undefined}
                decoding="async"
                onLoad={handleLoad}
                onError={handleError}
                {...(crossOrigin ? { crossOrigin } : {})}
                {...(fill
                    ? {
                          style: {
                              position: 'absolute',
                              inset: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                          },
                      }
                    : { width, height })}
                {...forwardableRest}
            />
        );
    }

    const { alt: _altIgnored, ...forwardProps } = imageProps;

    return (
        <Image
            alt={resolvedAlt}
            {...forwardProps}
            onLoad={handleLoad}
            onError={handleError}
        />
    );
});

export default AppImage;
export { AppImage };
